import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSocket } from '../socket';
import { showAdBeforeCall } from '../utils/adUtils';
import { playRingingSound, stopRingingSound, vibrate } from '../utils/soundUtils';
import { showCallNotification } from '../utils/notificationUtils';
import { getIceServers } from '../utils/iceServers';
import api from '../api';

const VideoCall = ({
  partner,
  roomId,
  onEndCall,
  pendingIncomingCall,
  onPendingIncomingConsumed,
  embedded = false,
}) => {
  const { user } = useAuth();
  const [callStatus, setCallStatus] = useState('idle');
  const [incomingCall, setIncomingCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isCaller, setIsCaller] = useState(false);
  const [callError, setCallError] = useState(null);
  const [facingMode, setFacingMode] = useState('user');
  const callStatusRef = useRef('idle');

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerConnection = useRef(null);
  const pendingIceCandidates = useRef([]);
  const remoteMediaStreamRef = useRef(null);
  const roomIdRef = useRef(roomId);
  const partnerIdRef = useRef(partner?._id);
  const isCallerRef = useRef(false);

  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    partnerIdRef.current = partner?._id;
  }, [partner?._id]);

  const ICE_SERVERS = getIceServers();

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleIncomingCall = (data) => {
      if (data.from === user?._id) return;
      if (data.roomId && roomIdRef.current && data.roomId !== roomIdRef.current) return;
      setIncomingCall(data);
      setCallStatus('incoming');
      setIsCaller(false);
      playRingingSound();
      vibrate([300, 100, 300]);
      showCallNotification(data.fromNickname || partner?.nickname || 'Someone');
    };

    const handleCallAccepted = async () => {
      if (!isCallerRef.current) return;
      try {
        stopRingingSound();
        setCallStatus('connecting');
        await createOffer();
      } catch (error) {
        console.error('Error on call accepted:', error);
        stopRingingSound();
        setCallStatus('idle');
      }
    };

    const handleCallRejected = (data) => {
      if (data?.roomId && roomIdRef.current && data.roomId !== roomIdRef.current) return;
      stopRingingSound();
      cleanupCall();
      setIncomingCall(null);
      setCallStatus('idle');
      if (onEndCall) onEndCall();
      alert(`${partner?.nickname || 'User'} rejected the call`);
    };

    const handleVideoOffer = (data) => {
      handleOffer(data);
    };

    const handleVideoAnswer = (data) => {
      handleAnswer(data);
    };

    const handleVideoIceCandidate = (data) => {
      handleIceCandidate(data);
    };

    const handleVideoCallEnded = (data) => {
      if (data?.roomId && roomIdRef.current && data.roomId !== roomIdRef.current) return;
      stopRingingSound();
      cleanupCall();
      setCallStatus('idle');
      setIncomingCall(null);
      if (onEndCall) onEndCall();
    };

    socket.on('video_offer', handleVideoOffer);
    socket.on('video_answer', handleVideoAnswer);
    socket.on('ice_candidate', handleVideoIceCandidate);
    socket.on('call_incoming', handleIncomingCall);
    socket.on('call_accepted', handleCallAccepted);
    socket.on('call_rejected', handleCallRejected);
    socket.on('video_call_ended', handleVideoCallEnded);

    return () => {
      socket.off('video_offer', handleVideoOffer);
      socket.off('video_answer', handleVideoAnswer);
      socket.off('ice_candidate', handleVideoIceCandidate);
      socket.off('call_incoming', handleIncomingCall);
      socket.off('call_accepted', handleCallAccepted);
      socket.off('call_rejected', handleCallRejected);
      socket.off('video_call_ended', handleVideoCallEnded);
    };
  }, [onEndCall, partner?._id, roomId, user?._id]);

  useEffect(() => () => cleanupCall(), []);

  const attachStreamToVideo = (el, stream, muted = false) => {
    if (!el || !stream) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    el.muted = muted;
    if (!muted) {
      el.volume = 1.0;
      el.defaultMuted = false;
    }
    el.play().catch((error) => {
      // Ignore AbortError when video element unmounts or interrupts its own play
      if (error.name !== 'AbortError') {
        console.error('Video autoplay blocked:', error);
      }
    });
  };

  const bindLocalVideoRef = (el) => {
    localVideoRef.current = el;
    attachStreamToVideo(el, localStream, true);
  };

  const bindRemoteVideoRef = (el) => {
    remoteVideoRef.current = el;
    // Video element is display-only — audio plays through <audio> to avoid echo/double sound
    attachStreamToVideo(el, remoteStream, true);
  };

  const bindRemoteAudioRef = (el) => {
    remoteAudioRef.current = el;
    attachStreamToVideo(el, remoteStream, false);
  };

  useEffect(() => {
    attachStreamToVideo(localVideoRef.current, localStream, true);
  }, [localStream, callStatus]);

  useEffect(() => {
    attachStreamToVideo(remoteVideoRef.current, remoteStream, true);
    attachStreamToVideo(remoteAudioRef.current, remoteStream, false);
  }, [remoteStream, callStatus]);

  useEffect(() => {
    if (!pendingIncomingCall || !roomId) return;
    if (pendingIncomingCall.roomId !== roomId) return;
    if (callStatus !== 'idle') return;

    if (pendingIncomingCall.accept) {
      (async () => {
        try {
          setCallError(null);
          if (!localStream) await ensureLocalMedia();
          const socket = getSocket();
          if (socket) {
            socket.emit('call_accept', {
              roomId: roomIdRef.current,
              from: user._id,
              to: pendingIncomingCall.from,
            });
          }
          isCallerRef.current = false;
          setIsCaller(false);
          setIncomingCall(null);
          setCallStatus('connecting');
        } catch (error) {
          console.error('Auto-accept call error:', error);
          setCallError(error.message || 'Could not access camera/microphone.');
          setCallStatus('incoming');
          setIncomingCall({
            from: pendingIncomingCall.from,
            fromNickname: pendingIncomingCall.fromNickname || partner?.nickname,
            roomId: pendingIncomingCall.roomId,
          });
        } finally {
          onPendingIncomingConsumed?.();
        }
      })();
      return;
    }

    setIncomingCall({
      from: pendingIncomingCall.from,
      fromNickname: pendingIncomingCall.fromNickname || partner?.nickname,
      roomId: pendingIncomingCall.roomId,
    });
    setCallStatus('incoming');
    setIsCaller(false);
    onPendingIncomingConsumed?.();
  }, [pendingIncomingCall, roomId, partner?.nickname, callStatus, onPendingIncomingConsumed]);

  const incrementCallCount = async () => {
    try {
      await api.post('/users/increment-call', { userId: user._id });
    } catch (error) {
      console.error('Error incrementing call count:', error);
    }
  };

  const ensureLocalMedia = async (facing = facingMode) => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        setIsVideoOff(!videoTrack.enabled);
      }
      return localStream;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: { ideal: 1 },
        },
      });
      setLocalStream(stream);
      const videoTrack = stream.getVideoTracks()[0];
      setIsVideoOff(videoTrack ? !videoTrack.enabled : false);
      return stream;
    } catch (error) {
      console.error('Camera/Microphone permission denied:', error.name);
      throw new Error('Camera/Microphone access denied. Please allow permissions and try again.');
    }
  };

  const flipCamera = async () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    try {
      if (localStream) {
        localStream.getTracks().forEach((t) => t.stop());
      }
      setLocalStream(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: next },
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: { ideal: 1 },
        },
      });
      setLocalStream(stream);
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      setIsVideoOff(videoTrack ? !videoTrack.enabled : false);
      if (peerConnection.current) {
        const videoSender = peerConnection.current.getSenders().find((s) => s.track?.kind === 'video');
        const audioSender = peerConnection.current.getSenders().find((s) => s.track?.kind === 'audio');
        const newTrack = stream.getVideoTracks()[0];
        if (videoSender && newTrack) await videoSender.replaceTrack(newTrack);
        if (audioSender && audioTrack) await audioSender.replaceTrack(audioTrack);
      }
    } catch (error) {
      console.error('Flip camera error:', error);
      setCallError('Could not switch camera');
    }
  };

  const ensurePeerConnection = (targetUserId) => {
    if (peerConnection.current) return peerConnection.current;

    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.ontrack = (event) => {
      if (event.streams?.[0]) {
        remoteMediaStreamRef.current = event.streams[0];
      } else {
        if (!remoteMediaStreamRef.current) {
          remoteMediaStreamRef.current = new MediaStream();
        }
        const stream = remoteMediaStreamRef.current;
        if (!stream.getTracks().some((t) => t.id === event.track.id)) {
          stream.addTrack(event.track);
        }
      }
      const stream = remoteMediaStreamRef.current;
      setRemoteStream(stream);
      attachStreamToVideo(remoteVideoRef.current, stream, true);
      attachStreamToVideo(remoteAudioRef.current, stream, false);
      setCallStatus('connected');
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
      } else if (pc.connectionState === 'failed') {
        setCallError('Connection failed. Check network or try again.');
        setCallStatus('idle');
        cleanupCall();
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const socket = getSocket();
        if (socket) {
          socket.emit('ice_candidate', {
            roomId: roomIdRef.current,
            candidate: event.candidate,
            to: targetUserId,
            from: user._id
          });
        }
      }
    };

    peerConnection.current = pc;
    return pc;
  };

  const flushPendingCandidates = async () => {
    if (!peerConnection.current || pendingIceCandidates.current.length === 0) return;
    const toApply = [...pendingIceCandidates.current];
    pendingIceCandidates.current = [];
    for (const candidate of toApply) {
      await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
    }
  };

  const createOffer = async () => {
    try {
      setCallError(null);
      const stream = await ensureLocalMedia();
      const pc = ensurePeerConnection(partner?._id);
      if (!pc.getSenders().length) {
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const socket = getSocket();
      if (socket) {
        // Send offer to specific user (partner._id) for direct routing
        socket.emit('video_offer', {
          roomId: roomIdRef.current,
          offer,
          to: partnerIdRef.current,
          from: user._id
        });
      }
      await incrementCallCount();
    } catch (error) {
      console.error('Error creating offer:', error);
      stopRingingSound();
      setCallStatus('idle');
      setCallError('Failed to setup video call: ' + error.message);
    }
  };

  const handleOffer = async (data) => {
    try {
      if (!data?.offer || data.offer.type !== 'offer') {
        console.warn('Ignoring invalid video offer payload:', data);
        return;
      }
      setCallError(null);
      const stream = await ensureLocalMedia();
      const pc = ensurePeerConnection(data.from);
      if (pc.signalingState !== 'stable') {
        console.warn('Ignoring video offer while signaling state is:', pc.signalingState);
        return;
      }
      if (!pc.getSenders().length) {
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      }

      // Use plain object format for RTCSessionDescription
      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
      if (pc.signalingState !== 'have-remote-offer') {
        console.warn('Unexpected signaling state after remote offer:', pc.signalingState);
        return;
      }
      await flushPendingCandidates();

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      const socket = getSocket();
      if (socket) {
        socket.emit('video_answer', {
          roomId: roomIdRef.current,
          answer,
          to: data.from,
          from: user._id
        });
      }
      setCallStatus('connecting');
    } catch (error) {
      console.error('Error handling offer:', error);
      stopRingingSound();
      setCallStatus('idle');
      setCallError('Failed to connect video call: ' + error.message);
    }
  };

  const handleAnswer = async (data) => {
    try {
      if (!data?.answer || data.answer.type !== 'answer') return;
      const pc = peerConnection.current;
      if (!pc || pc.signalingState !== 'have-local-offer') return;
      await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
      await flushPendingCandidates();
      setCallStatus('connected');
    } catch (error) {
      console.error('Error handling answer:', error);
      setCallError('Failed to connect: ' + error.message);
    }
  };

  const handleIceCandidate = async (data) => {
    try {
      if (!data?.candidate) return;
      const pc = peerConnection.current;
      if (!pc || !pc.remoteDescription) {
        pendingIceCandidates.current.push(data.candidate);
        return;
      }
      await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
    } catch (error) {
      console.error('Error handling ICE candidate:', error);
    }
  };

  const cleanupCall = () => {
    stopRingingSound();
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    if (localStream) {
      localStream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.error('Error stopping track:', error);
        }
      });
    }
    if (peerConnection.current) {
      peerConnection.current.close();
    }
    setLocalStream(null);
    setRemoteStream(null);
    remoteMediaStreamRef.current = null;
    peerConnection.current = null;
    pendingIceCandidates.current = [];
    setIsMuted(false);
    setIsVideoOff(false);
    isCallerRef.current = false;
    setIsCaller(false);
    setCallError(null);
  };

  const endCall = () => {
    const socket = getSocket();
    if (socket) {
      socket.emit('video_call_end', {
        roomId,
        from: user._id,
        to: partnerIdRef.current || incomingCall?.from
      });
    }
    cleanupCall();
    setCallStatus('ended');
    setIncomingCall(null);
    if (onEndCall) onEndCall();
  };

  const requestCall = () => {
    const socket = getSocket();
    if (!socket || !roomId || !partner?._id) return;
    isCallerRef.current = true;
    setIsCaller(true);
    setCallStatus('calling');
    playRingingSound(); // Play ringing sound for caller
    socket.emit('call_request', {
      roomId,
      from: user._id,
      fromNickname: user.nickname,
      to: partner?._id
    });
  };

  const acceptIncomingCall = async () => {
    try {
      stopRingingSound();
      setCallError(null);
      
      // Get media permission first
      if (!localStream) {
        await ensureLocalMedia();
      }
      
      const socket = getSocket();
      const calleeTarget = incomingCall?.from || partnerIdRef.current;
      if (socket) {
        socket.emit('call_accept', { roomId: roomIdRef.current, from: user._id, to: calleeTarget });
      }
      isCallerRef.current = false;
      setIsCaller(false);
      setIncomingCall(null);
      setCallStatus('connecting');
    } catch (error) {
      console.error('Error accepting call:', error);
      stopRingingSound();
      setCallError(error.message || 'Could not access camera/microphone. Please check browser permissions.');
      setCallStatus('incoming');
    }
  };

  const rejectIncomingCall = () => {
    stopRingingSound(); // Stop ringing when rejecting
    const socket = getSocket();
    if (socket) {
      socket.emit('call_reject', {
        roomId,
        from: user._id,
        to: incomingCall?.from || partnerIdRef.current
      });
    }
    setIncomingCall(null);
    cleanupCall();
    setCallStatus('idle');
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => { track.enabled = !track.enabled; });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => { track.enabled = !track.enabled; });
      setIsVideoOff(!isVideoOff);
    }
  };

  const initiateCall = async () => {
    try {
      setCallError(null);
      // Get media permission FIRST before calling
      await ensureLocalMedia();
      if ((user?.freeCallsUsed ?? 0) < 1) {
        requestCall();
      } else {
        showAdBeforeCall(() => { requestCall(); });
      }
    } catch (error) {
      console.error('Camera/microphone error:', error);
      setCallError(error.message || 'Camera/microphone access denied');
    }
  };

  const shellClass = embedded
    ? 'flex flex-col items-center justify-start p-2 bg-base-200 h-full w-full'
    : 'flex flex-col items-center justify-center min-h-[400px] p-6 bg-base-200 rounded-xl';

  return (
    <div className={shellClass}>
      {callStatus === 'idle' && (
        <div className="card bg-base-100 shadow-lg p-8 flex flex-col items-center gap-4">
          {callError && (
            <div className="alert alert-error text-sm w-full">
              <span>{callError}</span>
              <button 
                className="btn btn-sm btn-ghost" 
                onClick={() => setCallError(null)}
              >
                ✕
              </button>
            </div>
          )}
          <div className="avatar placeholder">
            <div className={`w-20 rounded-full ${partner?.gender === 'Male' ? 'bg-info/20 text-info' : 'bg-secondary/20 text-secondary'}`}>
              <span className="text-3xl">{partner?.nickname?.charAt(0).toUpperCase()}</span>
            </div>
          </div>
          <h3 className="font-bold text-lg">{partner?.nickname}</h3>
          <span className="badge badge-outline">
            {partner?.gender === 'Male' ? '👨' : '👩'} {partner?.gender}
          </span>
          <button className="btn btn-primary gap-2 mt-2" onClick={initiateCall}>
            📹 Start Video Call
          </button>
          <div className="divider my-2 w-full" />
          <div className="alert alert-info text-sm">
            <span>🔒 Camera/mic permissions needed. Browser will ask for permission when you click the button above.</span>
          </div>
          {(user?.freeCallsUsed ?? 0) >= 1 && (
            <p className="text-xs text-base-content/50">* Ad will play before call starts</p>
          )}
        </div>
      )}

      {callStatus === 'incoming' && (
        <div className="card bg-base-100 shadow-lg p-8 flex flex-col items-center gap-4">
          <h3 className="font-bold text-lg animate-pulse">📹 Incoming Call</h3>
          <p className="text-sm text-base-content/70">{incomingCall?.fromNickname || partner?.nickname} is calling you</p>
          <div className="flex gap-3">
            <button className="btn btn-success btn-sm" onClick={acceptIncomingCall}>Accept</button>
            <button className="btn btn-error btn-sm" onClick={rejectIncomingCall}>Reject</button>
          </div>
        </div>
      )}

      {callStatus === 'calling' && (
        <div className="card bg-base-100 shadow-lg p-8 flex flex-col items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="avatar placeholder">
              <div className={`w-14 rounded-full ${user?.gender === 'Male' ? 'bg-info/20 text-info' : 'bg-secondary/20 text-secondary'}`}>
                <span className="text-xl">{user?.nickname?.charAt(0).toUpperCase()}</span>
              </div>
            </div>
            <span className="loading loading-dots loading-md text-primary" />
            <div className="avatar placeholder">
              <div className={`w-14 rounded-full ${partner?.gender === 'Male' ? 'bg-info/20 text-info' : 'bg-secondary/20 text-secondary'}`}>
                <span className="text-xl">{partner?.nickname?.charAt(0).toUpperCase()}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-base-content/70">Calling {partner?.nickname}...</p>
          <button className="btn btn-error btn-outline btn-sm" onClick={endCall}>Cancel</button>
        </div>
      )}

      {callStatus === 'connecting' && (
        <div className="card bg-base-100 shadow-lg p-8 flex flex-col items-center gap-4 w-full">
          {localStream && (
            <div className="relative w-full max-w-sm aspect-video bg-black rounded-lg overflow-hidden">
              <video ref={bindLocalVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            </div>
          )}
          <span className="loading loading-ring loading-md text-primary" />
          <p className="text-sm text-base-content/70">Connecting live video...</p>
          <button className="btn btn-error btn-outline btn-sm" onClick={endCall}>Cancel</button>
        </div>
      )}

      {callStatus === 'connected' && (
        <div className="w-full flex flex-col items-center gap-2 flex-1">
          <div
            className={
              embedded
                ? 'relative w-full h-full bg-black rounded-xl overflow-hidden'
                : 'relative w-full bg-black rounded-xl overflow-hidden max-w-3xl aspect-video'
            }
          >
            <audio ref={bindRemoteAudioRef} autoPlay playsInline />
            <video
              ref={bindRemoteVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <span className="absolute top-2 left-2 badge badge-neutral badge-sm">{partner?.nickname}</span>
            <div className="absolute bottom-3 right-3 w-24 sm:w-32 aspect-video bg-base-300 rounded-lg overflow-hidden border-2 border-base-100 shadow-lg">
              <video
                ref={bindLocalVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
              />
              <span className="absolute bottom-1 left-1 badge badge-neutral badge-xs">You</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap justify-center">
            <button type="button" className={`btn btn-circle btn-sm ${isMuted ? 'btn-warning' : 'btn-ghost'}`} onClick={toggleMute}>
              {isMuted ? '🔇' : '🔊'}
            </button>
            <button type="button" className={`btn btn-circle btn-sm ${isVideoOff ? 'btn-warning' : 'btn-ghost'}`} onClick={toggleVideo}>
              {isVideoOff ? '📷' : '📹'}
            </button>
            <button type="button" className="btn btn-circle btn-sm btn-ghost" onClick={flipCamera} title="Switch front/back camera">
              🔄
            </button>
            <button type="button" className="btn btn-circle btn-sm btn-error" onClick={endCall}>
              📞
            </button>
          </div>
        </div>
      )}

      {callStatus === 'ended' && (
        <div className="card bg-base-100 shadow-lg p-8 flex flex-col items-center gap-4">
          <h3 className="font-bold text-lg">Call Ended</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setCallStatus('idle')}>Start New Call</button>
        </div>
      )}
    </div>
  );
};

export default VideoCall;
